import { useTranslation } from 'react-i18next';
import { useGetOrderEventsQuery } from '../api/booking/orderEventsApiSlice';
import {
    customerMailTooltip,
    readCustomerMailHistory,
} from '../flows/customerMailHistory';
import { formatAtStation, zoneOfLocation } from '../utils/stationTime';
import type { CustomerMailBlockReason } from '../utils/customerMailGuard';

/** What the booking-details badge renders. */
export type MailBadgeType = 'sent' | 'not_sent' | 'change_not_sent' | 'mail_blocked';

export interface CustomerMailBadge {
    type: MailBadgeType;
    /** Detail for the tooltip; empty string when there is nothing to say. */
    tooltip: string;
    /** `mail_blocked` only: why the payment rule refused the mail, for the fix link. */
    blockedReason?: CustomerMailBlockReason;
}

/**
 * The three-state mail badge and its tooltip (#1993).
 *
 * NOT lazy, unlike `useCustomerMailHint`: this one is the badge itself, visible
 * the moment the page opens, so the read has to have happened by then.
 *
 * **Degrades to exactly today's behaviour.** While the events are loading, or
 * if the read fails, or on a booking written before the log existed, the type
 * comes from `isMailSent` alone — the same boolean the badge used before this
 * hook existed — and the tooltip stays empty rather than asserting something
 * about data we do not have. Only a positive finding in the log can upgrade the
 * badge to `change_not_sent`.
 *
 * The one case where the log overrides the boolean is deliberate: `is_mail_sent`
 * records the LAST attempt, so an edit saved without a mail leaves it `true`
 * from the original confirmation. That is exactly the state this badge exists
 * to correct.
 */
export const useCustomerMailState = (
    orderId: string | undefined,
    isMailSent: boolean | undefined,
    /** The booking's `locationTimeZone` (#3219); the station default when absent. */
    locationTimeZone?: string,
): CustomerMailBadge => {
    const { t, i18n } = useTranslation();

    const fallback: CustomerMailBadge = {
        type: isMailSent === true ? 'sent' : 'not_sent',
        tooltip: '',
    };

    const { data, isError } = useGetOrderEventsQuery(
        { orderId: orderId ?? '' },
        { skip: !orderId },
    );

    if (isError || !data) return fallback;

    // Same locale split as `InvoiceOptionsDialog`, plus the time — "which mail,
    // when" is the question, and a date alone cannot separate two edits on one
    // day, which is the case an operator is most likely to be looking at.
    const language = (i18n.language || 'en').toLowerCase();
    const pattern = language.startsWith('de')
        ? 'dd.MM.yyyy, HH:mm'
        : 'MM/dd/yyyy, HH:mm';
    const timeZone = zoneOfLocation(locationTimeZone);
    const formatDateTime = (iso: string) => formatAtStation(iso, pattern, 'en', timeZone);

    const history = readCustomerMailHistory(data.events);
    const tooltipParts = customerMailTooltip(history, formatDateTime);
    const tooltip = tooltipParts
        ? t(tooltipParts.messageKey, tooltipParts.values)
        : '';

    // `never` is not promoted over the boolean. A booking created before the
    // event log existed has no events at all and would otherwise flip to
    // "never mailed" on a booking whose confirmation demonstrably went out —
    // the log only knows what it has recorded, and saying more than that is
    // the claim `customerMailHint` is careful to avoid in its wording.
    // The payment rule refused the latest mail and none went out since (PO,
    // 2026-10-30): "Nicht gesendet – <reason>", with the fix link — never the
    // plain "not sent", which reads like a skip or a fault.
    if (history.blockedReason) {
        return { type: 'mail_blocked', tooltip, blockedReason: history.blockedReason };
    }
    if (history.state === 'outdated') return { type: 'change_not_sent', tooltip };
    if (history.state === 'current') return { type: 'sent', tooltip };
    return { ...fallback, tooltip };
};
